import React, { useMemo, useState } from 'react';
import { Users } from 'lucide-react';
import NumericInput from './NumericInput';
import { ADJUSTMENT_YEARS } from './forecastAdjustments';

// OP_03W · Detailed headcount planning.
//
// Roster mode replaces the generic vacancy factor with employee-level
// decisions: retain / terminate / transfer each person, with adjustable
// fringe, incentive %, and merit % by year (baseline vs plan). The comp delta
// vs the baseline-assumption roster feeds the model like a comp lever.

export const DEFAULT_HR_PLAN = {
  mode: 'vacancy',                       // 'vacancy' | 'roster'
  keepVacancyFactor: true,               // roster mode: additive vs replacing
  fringePct: 28.0,
  incentivePctByYear: ADJUSTMENT_YEARS.map(() => null),   // null = role targets
  meritPctByYear: [3.6, 3.4, 3.2, 3.2, 3.0],
  actions: {}                            // employeeId -> {action, month (1-60), transferTo}
};

export const HR_BASELINE = { fringePct: 28.0, meritPct: 3.5 };

// Annual roster comp ($MM) for forecast years under a given assumption set.
export const rosterCompByYear = (roster, plan, useBaselineAssumptions = false) => {
  if (!roster) return ADJUSTMENT_YEARS.map(() => 0);
  const fringe = (useBaselineAssumptions ? HR_BASELINE.fringePct : plan.fringePct) / 100;
  return ADJUSTMENT_YEARS.map((year, yearIndex) => {
    // Cumulative merit from forecast start through this year.
    let meritFactor = 1;
    for (let i = 0; i <= yearIndex; i += 1) {
      const merit = useBaselineAssumptions
        ? HR_BASELINE.meritPct
        : (plan.meritPctByYear[i] ?? HR_BASELINE.meritPct);
      meritFactor *= 1 + merit / 100;
    }
    let total = 0;
    roster.forEach((employee) => {
      const action = useBaselineAssumptions ? null : plan.actions[employee.employeeId];
      let monthsActive = 12;
      if (action && (action.action === 'terminate' || action.action === 'transfer')) {
        const effectiveMonth = action.month || 1;          // 1-60 model months
        const yearOfAction = Math.floor((effectiveMonth - 1) / 12);
        if (yearIndex > yearOfAction) monthsActive = 0;
        else if (yearIndex === yearOfAction) monthsActive = ((effectiveMonth - 1) % 12);
      }
      if (monthsActive === 0) return;
      const incentive = useBaselineAssumptions || plan.incentivePctByYear[yearIndex] === null
        ? employee.incentiveTargetPct
        : plan.incentivePctByYear[yearIndex] / 100;
      const annual = employee.baseSalary
        * (employee.meritEligible ? meritFactor : 1)
        * (1 + fringe + incentive);
      total += annual * (monthsActive / 12);
    });
    return total / 1e6;
  });
};

const MONTH_OPTIONS = Array.from({ length: 24 }, (_, m) => ({
  value: m + 1,
  label: `FY${2027 + Math.floor(m / 12)} M${String((m % 12) + 1).padStart(2, '0')}`
}));

const ForecastHrRoster = ({ roster, summary, plan, onPlanChange, markets, disabled }) => {
  const [showAll, setShowAll] = useState(false);

  const baselineComp = useMemo(() => rosterCompByYear(roster, plan, true), [roster, plan]);
  const planComp = useMemo(() => rosterCompByYear(roster, plan, false), [roster, plan]);

  const actionCount = Object.values(plan.actions).filter((a) => a.action !== 'retain').length;
  const visible = showAll ? roster : roster.slice(0, 12);

  const setAction = (employeeId, patch) => {
    const previous = plan.actions[employeeId] || { action: 'retain', month: 1 };
    onPlanChange(
      { ...plan, actions: { ...plan.actions, [employeeId]: { ...previous, ...patch } } },
      `Roster action: ${employeeId}`
    );
  };

  return (
    <div className="forecast-subpanel hr-roster">
      <div className="forecast-panel-head">
        <span className="forecast-section-code">OP_03W · Detailed headcount plan</span>
        <h3>
          <Users size={15} style={{ marginRight: 6, verticalAlign: '-2px' }} />
          {summary.headcount} employees · roster loaded comp ties to GL Personnel Compensation
        </h3>
      </div>

      <div className="forecast-submit-chips">
        <span className="forecast-health-chip neutral">
          Baseline roster comp FY{ADJUSTMENT_YEARS[0]}: ${baselineComp[0].toFixed(1)}MM
        </span>
        <span className={`forecast-health-chip ${planComp[0] - baselineComp[0] <= 0 ? 'green' : 'yellow'}`}>
          Plan Δ FY{ADJUSTMENT_YEARS[0]}: {planComp[0] - baselineComp[0] >= 0 ? '+' : ''}
          {(planComp[0] - baselineComp[0]).toFixed(2)}MM
        </span>
        <span className="forecast-health-chip neutral">{actionCount} employee action(s)</span>
        <label className="hr-keep-vacancy">
          <input
            type="checkbox"
            checked={plan.keepVacancyFactor}
            disabled={disabled}
            onChange={(event) =>
              onPlanChange({ ...plan, keepVacancyFactor: event.target.checked }, 'Roster vacancy mode')
            }
          />
          Also keep generic vacancy factor (uncheck to fully replace it)
        </label>
      </div>

      <div className="hr-assumptions">
        <div className="hr-assumption-block">
          <span>Fringe benefit load</span>
          <small>baseline {HR_BASELINE.fringePct.toFixed(1)}%</small>
          <NumericInput
            value={plan.fringePct} step={0.5} min={15} max={45} disabled={disabled}
            onCommit={(value) => onPlanChange({ ...plan, fringePct: value }, 'Roster fringe %')}
          />
        </div>
        <div className="hr-assumption-block wide">
          <span>Merit % by year</span>
          <small>baseline {HR_BASELINE.meritPct.toFixed(1)}%/yr · defaults pre-filled</small>
          <div className="hr-year-row">
            {ADJUSTMENT_YEARS.map((year, yearIndex) => (
              <label key={year}>
                <em>FY{String(year).slice(2)}</em>
                <NumericInput
                  value={plan.meritPctByYear[yearIndex]} step={0.1} min={0} max={8} disabled={disabled}
                  onCommit={(value) => {
                    const next = plan.meritPctByYear.map((v, i) => (i === yearIndex ? value : v));
                    onPlanChange({ ...plan, meritPctByYear: next }, `Roster merit FY${year}`);
                  }}
                />
              </label>
            ))}
          </div>
        </div>
        <div className="hr-assumption-block wide">
          <span>Incentive % by year</span>
          <small>blank = each role's target (8–35%); enter to override all roles</small>
          <div className="hr-year-row">
            {ADJUSTMENT_YEARS.map((year, yearIndex) => (
              <label key={year}>
                <em>FY{String(year).slice(2)}</em>
                <NumericInput
                  value={plan.incentivePctByYear[yearIndex] ?? ''} step={0.5} min={0} max={50}
                  placeholder="role" disabled={disabled}
                  onCommit={(value) => {
                    const next = plan.incentivePctByYear.map((v, i) => (i === yearIndex ? value : v));
                    onPlanChange({ ...plan, incentivePctByYear: next }, `Roster incentive FY${year}`);
                  }}
                />
              </label>
            ))}
          </div>
        </div>
      </div>

      <div className="forecast-home-table-wrap hr-roster-table">
        <table className="forecast-home-table">
          <thead>
            <tr>
              <th>Employee</th>
              <th>Title</th>
              <th>Base salary</th>
              <th>Incentive target</th>
              <th>Action</th>
              <th>Effective</th>
              <th>Transfer to</th>
            </tr>
          </thead>
          <tbody>
            {visible.map((employee) => {
              const action = plan.actions[employee.employeeId] || { action: 'retain', month: 1 };
              return (
                <tr key={employee.employeeId} className={action.action !== 'retain' ? `hr-${action.action}` : ''}>
                  <td><strong>{employee.name}</strong></td>
                  <td>{employee.title}</td>
                  <td className="forecast-mono">${Math.round(employee.baseSalary / 1000)}k</td>
                  <td className="forecast-mono">{(employee.incentiveTargetPct * 100).toFixed(0)}%</td>
                  <td>
                    <select
                      value={action.action} disabled={disabled}
                      onChange={(event) => setAction(employee.employeeId, { action: event.target.value })}
                    >
                      <option value="retain">Retain</option>
                      <option value="terminate">Terminate</option>
                      <option value="transfer">Transfer</option>
                    </select>
                  </td>
                  <td>
                    {action.action !== 'retain' ? (
                      <select
                        value={action.month || 1} disabled={disabled}
                        onChange={(event) => setAction(employee.employeeId, { month: Number(event.target.value) })}
                      >
                        {MONTH_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                      </select>
                    ) : <span className="dim-note">—</span>}
                  </td>
                  <td>
                    {action.action === 'transfer' ? (
                      <select
                        value={action.transferTo || ''} disabled={disabled}
                        onChange={(event) => setAction(employee.employeeId, { transferTo: event.target.value })}
                      >
                        <option value="">Select market…</option>
                        {markets.map((market) => <option key={market} value={market}>{market}</option>)}
                      </select>
                    ) : <span className="dim-note">—</span>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {roster.length > 12 && (
        <button type="button" className="forecast-secondary-button compact" onClick={() => setShowAll((s) => !s)}>
          {showAll ? 'Show fewer' : `Show all ${roster.length} employees`}
        </button>
      )}

      <p className="forecast-submit-note">
        Terminations and transfers remove the employee's loaded comp from this unit's plan at the
        effective month (transfers land in the receiving market's cycle as a hire). The comp delta
        vs the baseline-assumption roster flows into the modified plan automatically and is recorded
        in lineage. Fictitious roster — seeded to tie to this unit's GL Personnel Compensation.
      </p>
    </div>
  );
};

export default ForecastHrRoster;
